import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { Microservices } from "../../app/AllowedMicroservices";
import { RootState } from "../../app/store";
import apiActionCreator, { HttpMethods } from "../../services/apiActionCreator";
import { FetchStatus } from "../common/enums";
import { IFilter } from "../common/interfaces";
import { IUser } from "./usersSlice";

const BASE_USER_ENDPOINT = "/user";

interface UsersPaginatedResponse {
  data: IUser[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface UsersPaginatedState {
  list: IUser[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  status: FetchStatus;
  filter: IFilter;
}

const initialState: UsersPaginatedState = {
  list: [],
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
  status: FetchStatus.Idle,
  filter: {
    text: "",
  },
};

export const usersPaginatedSlice = createSlice({
  name: "usersPaginated",
  initialState,
  reducers: {
    fetching: (state) => {
      state.status = FetchStatus.Fetching;
    },
    fetched: (state, action: PayloadAction<UsersPaginatedResponse>) => {
      state.list = action.payload.data || [];
      state.page = action.payload.page || 1;
      state.limit = action.payload.limit || state.limit;
      state.total = action.payload.total || 0;
      state.totalPages = action.payload.totalPages || 0;
      state.status = FetchStatus.Fetched;
    },
    error: (state) => {
      state.status = FetchStatus.Error;
    },
    setFilterText: (state, action: PayloadAction<string>) => {
      state.filter.text = action.payload;
    },
    setFilterCompanyId: (state, action: PayloadAction<string>) => {
      state.filter.companyId = action.payload;
    },
    clearFilter: (state) => {
      state.filter = { text: "" };
    },
  },
});

export const {
  fetching,
  fetched,
  error,
  setFilterText: setFilterTextForUsersPaginated,
  setFilterCompanyId: setFilterCompanyIdForUsersPaginated,
  clearFilter: clearFilterForUsersPaginated,
} = usersPaginatedSlice.actions;

export const fetchUsersPaginated = (params: {
  page?: number;
  limit?: number;
  companyId?: string;
  text?: string;
}) => (dispatch: Dispatch<object>) => {
  const query = new URLSearchParams();
  if (params.page) {
    query.set("page", String(params.page));
  }
  if (params.limit) {
    query.set("limit", String(params.limit));
  }
  if (params.companyId) {
    query.set("companyId", params.companyId);
  }
  if (params.text) {
    query.set("text", params.text);
  }
  return dispatch(
    apiActionCreator({
      endpoint: `${BASE_USER_ENDPOINT}/paginated?${query.toString()}`,
      types: {
        requestType: fetching,
        successTypes: [
          {
            actionOrCreator: fetched,
          },
        ],
        errorTypes: [
          {
            actionOrCreator: error,
          },
        ],
      },
      method: HttpMethods.GET,
      microservice: Microservices.USER_SERVICE,
      authenticated: true,
    })
  );
};

export const selectUsersPaginatedList = (state: RootState) => state.usersPaginated.list || [];
export const selectUsersPaginatedPage = (state: RootState) => state.usersPaginated.page;
export const selectUsersPaginatedLimit = (state: RootState) => state.usersPaginated.limit;
export const selectUsersPaginatedTotal = (state: RootState) => state.usersPaginated.total;
export const selectUsersPaginatedTotalPages = (state: RootState) => state.usersPaginated.totalPages;
export const selectAreUsersPaginatedFetching = (state: RootState) =>
  state.usersPaginated.status === FetchStatus.Fetching;
export const selectFilterTextForUsersPaginated = (state: RootState) =>
  state.usersPaginated.filter.text;
export const selectFilterCompanyIdForUsersPaginated = (state: RootState) =>
  state.usersPaginated.filter.companyId;

export default usersPaginatedSlice.reducer;
